import type ShapeImage from '../../shapes/ShapeImage';
import CropControl from '../../controls/CropControl';
import Shape from '../Shape';

class CropShape extends Shape {
  image: Shape<ShapeImage>;

  constructor(image: Shape<ShapeImage>) {
    super({
      controller: image.controller,
      shapeName: 'rect',
      angle: 0,
      opacity: 0,
      disableSmartGuides: true,
      selectable: true,
      left: image.croppedLeft || image.left,
      top: image.croppedTop || image.top,
      scaleX: image.croppedScaleX || image.scaleX,
      scaleY: image.croppedScaleY || image.scaleY,
    });
    this.image = image;

    this.controls = ['tl', 'tr', 'bl', 'br', 'mt', 'ml', 'mr', 'mb'].reduce(
      (controls, position) => {
        controls[position] = new CropControl(position);
        return controls;
      },
      {}
    );

    this.set({
      height: image.height,
      width: image.width,
    });

    this.on('scaling', () => this.constrainScale());
    this.on('moving', () => this.constrainPosition());
  }

  overFrame() {
    // We don't want the crop shape to interact with frames
    return null;
  }

  hasEmbeddedText() {
    return false;
  }

  constrainScale() {
    const propsToChange: Partial<this> = {};

    const image = this.image.shape?.originalValues;
    if (!image) return;

    const right = this.left + this.getScaledWidth();
    const imageRight = image.left + image.scaledWidth;

    if (this.left < image.left) {
      propsToChange.scaleX = (right - image.left) / this.width;
      propsToChange.left = image.left;
    } else if (right > imageRight) {
      propsToChange.scaleX = (imageRight - this.left) / this.width;
    }

    const bottom = this.top + this.getScaledHeight();
    const imageBottom = image.top + image.scaledHeight;

    if (bottom > imageBottom) {
      propsToChange.scaleY = (imageBottom - this.top) / this.height;
    }
    if (this.top < image.top) {
      propsToChange.scaleY = (bottom - image.top) / this.height;
      propsToChange.top = image.top;
    }

    if (Object.keys(propsToChange).length) {
      this.set(propsToChange);
    }
  }

  constrainPosition() {
    const propsToChange: Partial<this> = {};

    const image = this.image.shape?.originalValues;
    if (!image) return;

    if (this.left < image.left) {
      propsToChange.left = image.left;
    }

    if (this.top < image.top) {
      propsToChange.top = image.top;
    }

    const right = this.left + this.getScaledWidth();
    const imageRight = image.left + image.scaledWidth;
    if (right > imageRight) {
      propsToChange.left =
        image.left + image.scaledWidth - this.getScaledWidth();
    }

    const bottom = this.top + this.getScaledHeight();
    const imageBottom = image.top + image.scaledHeight;
    if (bottom > imageBottom) {
      propsToChange.top =
        image.top + image.scaledHeight - this.getScaledHeight();
    }

    if (Object.keys(propsToChange).length) {
      this.set(propsToChange);
    }
  }

  drawGuidelines(ctx: CanvasRenderingContext2D) {
    const { left, top } = this;
    const width = this.getScaledWidth();
    const height = this.getScaledHeight();

    const bottom = top + height;
    const right = left + width;

    const firstVerticalThird = left + width / 3;
    const secondVerticalThird = right - width / 3;
    const firstHorizontalThird = top + height / 3;
    const secondHorizontalThird = bottom - height / 3;

    const zoom = this.canvas.getZoom();
    ctx.save();

    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1 / zoom;
    ctx.globalAlpha = 0.6;
    // First vertical third
    ctx.beginPath();
    ctx.moveTo(firstVerticalThird, top);
    ctx.lineTo(firstVerticalThird, bottom);
    ctx.stroke();

    // Second vertical third
    ctx.beginPath();
    ctx.moveTo(secondVerticalThird, top);
    ctx.lineTo(secondVerticalThird, bottom);
    ctx.stroke();

    // First horizontal third
    ctx.beginPath();
    ctx.moveTo(left, firstHorizontalThird);
    ctx.lineTo(right, firstHorizontalThird);
    ctx.stroke();

    // Second horizontal third
    ctx.beginPath();
    ctx.moveTo(left, secondHorizontalThird);
    ctx.lineTo(right, secondHorizontalThird);
    ctx.stroke();

    ctx.restore();
  }

  render(ctx: CanvasRenderingContext2D) {
    super.render(ctx);

    this.drawGuidelines(ctx);
  }
}

export default CropShape;
